/* ========================================================================= *
 * AI TOOLS — the registry of SR's Custom GPTs.
 *
 * Brian: "Load that gpt into the portal."
 *
 * ⚠️ THIS IS A LAUNCHER, AND IT HAS TO BE.
 *
 * A Custom GPT is hosted by ChatGPT and has no embed API — there is no iframe,
 * no widget and no key that would let this hub run one. So this module follows
 * the same discipline as Pricing Engine, Presentations and Top Tier Teams: it
 * is a DOOR, never a copy. See CLAUDE.md → "Pricing Engine — DEEP-LINK, not
 * embedded" for the pattern and why it exists.
 *
 * The alternative — rebuilding a summarizer inside the platform against the
 * backend's OpenAI key — is a different product with a per-call bill attached,
 * and it would sit behind the spend register rather than a nav link. Not this.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠️ ACCESS IS THE WHOLE CAVEAT, AND IT IS STATED ON SCREEN.
 *
 * Every GPT below is currently shared **"Only me"** — Brian's ChatGPT account
 * and nobody else's. That is not an oversight: ChatGPT's own share dialog now
 * reads *"Sharing GPTs with the public is no longer available,"* and a personal
 * Plus account offers no workspace to share into. A teammate who clicks one of
 * these links today gets ChatGPT's own "you do not have access."
 *
 * So `access` is a REQUIRED field and the page renders it loudly. A launcher
 * that shows the team a wall of links they cannot open is worse than no page,
 * because they'll each spend a minute finding that out privately. Sharing GPTs
 * across a team needs a ChatGPT Business/Enterprise workspace; until SR has
 * one, `owner-only` is the honest label and the page says what it would take.
 *
 * ⚠️ CORRECTION, 2026-09-09 — CREATING IS NOT THE CONSTRAINT, SHARING IS.
 * Matt McNulty's handoff package stated that building a Custom GPT needs a
 * Team/Business plan. Brian confirmed today that it does not: a personal
 * ChatGPT Plus account creates GPTs fine, and every GPT in this registry was
 * built on one. The Business workspace is needed only to SHARE one with the
 * team. Kept as a correction rather than a silent edit, because the wrong
 * version of it was the reason a build was nearly deferred.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠️ A REGISTERED TOOL IS NOT NECESSARILY A CREATED ONE.
 *
 * `status` distinguishes a GPT that EXISTS in ChatGPT from one whose package
 * is written and staged but which nobody has pasted into ChatGPT yet. The
 * second has no id, and an id is not something to invent: a fabricated
 * `g-…` resolves to somebody else's GPT or to a 404, which is precisely the
 * dead link this whole module is built to avoid. So a staged tool renders with
 * what it is waiting on, and no anchor at all.
 * ───────────────────────────────────────────────────────────────────────── */

export const STATUS = {
  /** The GPT exists in ChatGPT and has an id. It opens. */
  LIVE: 'live',
  /** The package is written and staged; nobody has created the GPT yet. No id. */
  NOT_CREATED: 'not-created',
}

export const ACCESS = {
  /** Only the owner's ChatGPT account can open it. Everyone else gets a wall. */
  OWNER_ONLY: 'owner-only',
  /** Shared inside a ChatGPT workspace — any signed-in SR member can open it. */
  TEAM: 'team',
}

export const ACCESS_META = {
  [ACCESS.OWNER_ONLY]: {
    label: 'Brian only',
    tone: 'warn',
    hint: 'Shared "Only me" in ChatGPT. Anyone else who opens this link will be refused by ChatGPT, not by us.',
  },
  [ACCESS.TEAM]: {
    label: 'Whole team',
    tone: 'ok',
    hint: 'Shared to the SR ChatGPT workspace — sign in with your SR account and it opens.',
  },
}

/**
 * ONE ENTRY PER GPT. Adding, reordering or retiring one is an edit to this
 * array alone — the page renders whatever is declared here, exactly the way
 * `lib/topTierTeams.js` and `lib/builders.js` work.
 *
 * `id` is the ChatGPT GPT id. The URL is DERIVED from it (`gptUrl`) rather than
 * pasted, so a slug that drifts cannot produce a dead link — ChatGPT resolves
 * on the id and ignores the trailing slug.
 *
 * `useIt` is the field that earns its place: a tool nobody knows when to reach
 * for is a tool nobody reaches for. It answers "when would I open this?", not
 * "what is it?".
 */
export const AI_TOOLS = [
  {
    id: 'g-6a9465ed52bc819184f9220ce036c1d3',
    slug: 'the-summarizer',
    name: 'The Summarizer',
    blurb: 'Name a championship — it goes and finds it, verifies it, and returns the crown record with a source on every field.',
    useIt: 'Working the champion hunt. Type "2026 Stanley Cup" and nothing else. It does the searching, the cross-checking and the second pass at the blanks — you read the record.',
    /* THE INPUT IS THE NAME. Brian: "It needs to limit my human input."
     * Measured on the 2026 Stanley Cup from a three-word prompt: it reached the
     * governing body, filled every field, corrected the clinching date the
     * recap's dateline had wrong, found the CHAMPIONSHIP-season roster rather
     * than next season's, and returned READY with "nothing left for a human". */
    keeps: [
      'It fetches — no pasting articles, no hunting for a source URL first',
      'Every field carries its OWN source, because the facts come from different pages',
      'A second pass aimed at the blanks — an unsearched gap is a skipped step, not a gap',
      'Official / governing body first, and confidence stated against the tier it reached',
      'A field it can’t establish stays empty and says where it looked — never filled from memory',
    ],
    modes: ['/crown', '/story', '/gaps', '/batch', '/compare', '/general'],
    access: ACCESS.OWNER_ONLY,
    owner: 'Brian Kass',
  },
  {
    id: 'g-6a90a7fbcd04819197af0f36fe61a7c2',
    slug: 'sr-artwork-engine-director',
    name: 'SR Artwork Engine Director',
    blurb: 'Turns a rough ring brief into a complete, valid SR Artwork Engine build: components, the 12 script boxes, and the keyed type.',
    useIt: 'Before you open the Builder — hand it the brief and get back the inputs to paste in, instead of facing 13 blank steps.',
    keeps: [
      'Speaks the Builder’s own vocabulary — the real steps, not invented ones',
      'Keyed type stays typed, never described',
    ],
    modes: [],
    access: ACCESS.OWNER_ONLY,
    owner: 'Brian Kass',
    /* The one entry with a real destination inside this app. */
    pairsWith: { to: '/artwork-studio', label: 'Open Artwork Studio' },
  },
  {
    id: 'g-6a90a461e8848191abc489e67e0d1491',
    slug: 'sr-bezel-drop-director',
    name: 'SR Bezel Drop Director',
    blurb: 'Turns an SR Bezel Drop brief into art direction a designer can execute. Knows the two drop modes, SR’s six metals and the locked layer.',
    useIt: 'Handing a drop to a designer — it produces the direction, not the artwork.',
    keeps: [
      'The six metals SR actually sells, by name',
      'The locked layer: SR mark, client crest, keyed championship type',
    ],
    modes: [],
    access: ACCESS.OWNER_ONLY,
    owner: 'Brian Kass',
  },
  {
    /* ⚠️ NO ID, DELIBERATELY. `ai-tools/ring-concept-gpt/README.md`, verbatim:
     * *"Working folder. Nothing here is installed yet."* The instruction field,
     * the seven knowledge files, the reference-set gate and the
     * generate → inject → verify → payload flow are all written; nobody has
     * pasted them into ChatGPT. Inventing a `g-…` here would resolve to a
     * stranger's GPT or a 404 — the exact failure this page documents. The id
     * arrives from `COMMISSIONING.md`'s last step and is one edit to this line. */
    id: '',
    status: STATUS.NOT_CREATED,
    slug: 'sr-ring-concept-generator',
    name: 'SR Ring Concept Generator',
    blurb: 'The reasoning partner behind a generated concept: it works a ring brief against SR’s house look, the closest delivered precedent and the locked layer, and hands back a Builder payload.',
    /* THE LEVELS ARE THE FRAME. After the 2026-09-09 rework, "generated
     * concept" is a real path in the Studio — one line in, records sourced,
     * receipts shown, build recorded. This GPT is the same operating mode with
     * the reasoning out loud, which is what it is FOR and what stops the two
     * surfaces reading as rivals. */
    useIt: 'Thinking a concept through before it becomes a build — a direction nobody has made yet, where you want the reasoning, the precedent it leaned on and the trade-offs stated. The Studio’s level 2 is the in-app version of the same job; it is where a build actually gets recorded.',
    keeps: [
      'The same locked layer as the Studio — SR maker’s mark, the client’s owned crest, keyed championship type',
      'The crest is INJECTED, never drawn: the model draws a registration guide, the owned file replaces it before the render',
      'The reference gate — SR’s house look transfers from SR’s images, so it refuses to generate with none attached',
      'The six real SR metals and the retirement table; a discontinued option is dropped, never remapped',
      'It emits the Builder payload, so a concept lands in the Studio as a real build rather than a screenshot',
    ],
    modes: [],
    access: ACCESS.OWNER_ONLY,
    owner: 'Brian Kass',
    /* What it is waiting on, in the words of the person who has to do it. */
    awaiting: 'Not created yet — the instruction field and the seven knowledge files are staged in ai-tools/ring-concept-gpt/. Paste them into a new Custom GPT, then send the id from the URL and this card goes live.',
    pairsWith: { to: '/artwork-studio', label: 'Open Artwork Studio — level 2' },
  },
]

/** Does this entry point at a GPT that actually exists? */
export function isLive(tool) {
  return (tool?.status || STATUS.LIVE) === STATUS.LIVE
}

/**
 * ⚠️ ONE SENTENCE, TWO PAGES — the anti-drift device.
 *
 * After the 2026-09-09 levels rework there are two ways to reach a generated
 * concept: the Studio's level 2 (in-app, records sourced, build recorded) and
 * the Ring Concept GPT (the reasoning out loud). If `/ai-tools` and
 * `/artwork-studio` describe that relationship in their own words, the two
 * descriptions drift and a rep is left deciding between what look like rival
 * products.
 *
 * So the sentence lives HERE and both surfaces render it verbatim.
 * `verify-aitools.mjs` asserts both import it rather than paraphrasing.
 */
export const STUDIO_RELATIONSHIP =
  'Generated concept in the Artwork Studio is the in-app path — it sources the records and records the build. The Ring Concept GPT is the same job with the reasoning out loud. Both obey the same locked layer, and in both the client’s crest is the real owned file, injected rather than drawn.'

/**
 * The canonical URL for a GPT. Derived from the id so the registry cannot hold
 * a half-stale link; ChatGPT resolves on the id and treats the slug as cosmetic.
 * Returns '' for a malformed entry rather than a URL that would 404 — a card
 * with no link renders as "not linkable yet", never as a dead button.
 */
export function gptUrl(tool) {
  const id = String(tool?.id || '').trim()
  if (!/^g-[a-z0-9]+$/i.test(id)) return ''
  const slug = String(tool?.slug || '').trim()
  return `https://chatgpt.com/g/${id}${slug ? `-${slug}` : ''}`
}

/** Access badge for a tool, defaulting to the CONSERVATIVE answer.
 *  An entry that forgets to declare `access` reads as owner-only rather than as
 *  team-wide, because telling somebody a tool is open when it is not is the
 *  failure mode that wastes their time. */
export function accessMeta(tool) {
  return ACCESS_META[tool?.access] || ACCESS_META[ACCESS.OWNER_ONLY]
}

/** True when at least one tool is still owner-only — drives the page notice. */
export function hasOwnerOnly(tools = AI_TOOLS) {
  return tools.some((t) => (t?.access || ACCESS.OWNER_ONLY) === ACCESS.OWNER_ONLY)
}
